import React, { useEffect, useState } from 'react';
import { BASE, authHeaders, jsonHeaders } from '../api';
import FestiveBanner from '../festive/FestiveBanner';
import { FESTIVALS, festivalRange, formatShortDate, previewDate } from '../festive/festivals.config';

// Settings → Firm & Branding → Festive Banner. The festival content (artwork,
// dates, greetings) is fixed and shipped with the app; the firm only chooses
// which festivals to show, how to sign off, and an optional note per festival.
// The banner then appears on every user's dashboard automatically for the
// festival's dates, and nobody can hide it.

const Switch = ({ on, onChange, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}
    style={{ position: 'relative', width: 38, height: 22, borderRadius: 22, border: 'none', padding: 0, cursor: 'pointer', background: on ? '#10b981' : '#cbd5e1', flex: 'none', transition: 'background .2s' }}>
    <span style={{ position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
  </button>
);

export default function FestiveBannerSettings({ T: theme, darkMode = false }) {
  const T = theme || { bg2: '#fff', bg3: '#f8faff', border: '#e8edf5', text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8', cardBg: '#fff', inputBg: '#fff', inputBorder: '#e2e8f0' };
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/festive-settings`, { headers: authHeaders() })
      .then(async r => { const j = await r.json().catch(() => null); if (!r.ok) throw new Error((j && j.message) || `HTTP ${r.status}`); setS(j); })
      .catch(e => setError(e.message));
  }, []);

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const r = await fetch(`${BASE}/api/festive-settings`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(s) });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error((j && j.message) || `HTTP ${r.status}`);
      setS(j); setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const setOff = (id, shown) => setS(x => ({ ...x, off: shown ? x.off.filter(k => k !== id) : [...x.off, id] }));
  const setNote = (id, v) => setS(x => ({ ...x, notes: { ...x.notes, [id]: v } }));

  const panel = { background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 14, overflow: 'hidden' };
  const input = { flex: 1, minWidth: 0, background: T.inputBg || T.bg3, border: `1px solid ${T.inputBorder || T.border}`, borderRadius: 10, padding: '8px 12px', fontSize: 13, color: T.text, outline: 'none' };
  const row = { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', flexWrap: 'wrap' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>Festive Banner</h3>
          <p style={{ fontSize: 12, color: T.text3, marginTop: 3, maxWidth: 640 }}>
            A greeting banner appears on every user's dashboard automatically during each festival and goes away when it ends. Nobody can hide it. You choose which festivals to show and how to sign off. The artwork, dates and wording are provided and kept up to date for you.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {saved && <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 600 }}>✓ Saved</span>}
          <button onClick={save} disabled={saving || !s} style={{ padding: '9px 20px', borderRadius: 12, border: 'none', background: '#0f1f45', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving || !s ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', borderRadius: 10, padding: '8px 12px', fontSize: 12.5, marginBottom: 12 }}>{error}</div>}
      {!s && !error && <p style={{ fontSize: 13, color: T.text3 }}>Loading…</p>}

      {s && (
        <>
          <div style={panel}>
            <div style={row}>
              <Switch on={s.enabled} label="Show festive banner" onChange={v => setS(x => ({ ...x, enabled: v }))} />
              <div style={{ flex: 1 }}>
                <b style={{ fontSize: 13.5, color: T.text }}>Show the festive banner on the dashboard</b>
                <div style={{ fontSize: 12, color: T.text3 }}>Turn this off to hide every festive banner for your whole firm.</div>
              </div>
            </div>
            <div style={{ ...row, borderTop: `1px solid ${T.border}` }}>
              <b style={{ fontSize: 13.5, color: T.text }}>Sign-off line</b>
              <input style={input} maxLength={120} value={s.sign_off} placeholder="Your firm name" onChange={e => setS(x => ({ ...x, sign_off: e.target.value }))} />
              <span style={{ fontSize: 12, color: T.text3 }}>Shown as “— Team {s.sign_off || '…'}”</span>
            </div>
          </div>

          {FESTIVALS.map(f => {
            const year = f.recur ? String(new Date().getFullYear()) : f.from.slice(0, 4);
            const [from, to] = festivalRange(f, year);
            const shown = !s.off.includes(f.id);
            return (
              <div key={f.id} style={{ ...panel, opacity: s.enabled ? 1 : 0.55 }}>
                <div style={row}>
                  <Switch on={shown} label={`Show ${f.name}`} onChange={v => setOff(f.id, v)} />
                  <div style={{ minWidth: 150 }}>
                    <b style={{ fontSize: 13.5, color: T.text }}>{f.name}</b>
                    <div style={{ fontSize: 12, color: T.text3 }}>
                      {from === to ? formatShortDate(from) : `${formatShortDate(from)} – ${formatShortDate(to)}`}{f.recur ? ' · every year' : ` · ${year}`}
                    </div>
                  </div>
                  <input style={input} maxLength={80} value={(s.notes || {})[f.id] || ''} placeholder="Optional note, e.g. Office closed on 8 Nov" onChange={e => setNote(f.id, e.target.value)} />
                </div>
                <div style={{ padding: '0 18px 16px', opacity: shown ? 1 : 0.45 }}>
                  <FestiveBanner festival={f} iso={previewDate(f)} signOff={s.sign_off} note={(s.notes || {})[f.id]} T={T} darkMode={darkMode} />
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
